<script lang="ts">
    import { onDestroy, onMount } from 'svelte';
    import { fade } from 'svelte/transition';

    import { onMapReady } from '../../utilities/onMapReady';
    import { filter } from '../../../stores/filter';
    import FilterAdapter from './FilterAdapter';
    import type { QueriesType } from './types';
    import IconFilterAlt from '../../shared/Icons/IconFilterAlt.svelte';
    import SearchInputLikeFilter from '../../shared/SearchInputLikeFilter.svelte';
    import { _ } from 'svelte-i18n';
    import SearchInput from '../../shared/SearchInput.svelte';
    import CheckboxLikeFilter from '../../shared/CheckboxLikeFilter.svelte';
    import type { LastMileConfigResponseType } from '../Settings/settings';
    import { debug, deliveryList } from '../../../stores/store';
    import type { FilterManagerIO } from './components/FilterByParameters/FilterManagerIO';
    import TelematicsFilterIconWithStats from './TelematicsFilterIconWithStats.svelte';
    import { features } from '../../shared/Features/features';
    import type {QuickFilterType} from '../../shared/QuickFilter.svelte';
    import QuickFilter from '../../shared/QuickFilter.svelte';


    export let collectionName: string;
    // export let queries: QueriesType;
    export let showQuickFilters: boolean = false;
    export let enableInactiveQueries: boolean;
    export let filterConfig: LastMileConfigResponseType | null;
    export let filterManagerIO: FilterManagerIO;
    export let visibleFilterIcon: boolean = true;
    export let andOrSelectionEnabled: boolean = false;
    export let forceReload: boolean = false;
    export let totalCount: number = 0;
    export let filteredCount: number = 0;
    export let makeBlured: boolean = false;
    export let showButtonRemoveAll: boolean = true;
    export let showButtonAddFilter: boolean = true;
    export let showButtonRestoreFilter: boolean = true;
    export let quickFilters: QuickFilterType[] = [];
    export let onQuickFilterSwitch: (index: number, active: boolean) => void = (index: number, active: boolean) => {};

    export let onChange: (
        queries: QueriesType,
        andOrSelection: string,
        allQueries: QueriesType,
        changed: boolean
    ) => void;

    let root: HTMLDivElement;
    let filterAdapter: FilterAdapter;
    let debugAllQueries: QueriesType = {};
    // let filterHidden: boolean = false;

    function reload() {
        Promise.all([onMapReady()]).then(() => {
            if (!filterConfig) {
                console.error('TelematicsFilter, filterConfig is not defined');
                return;
            }
            if (!root) {
                console.error('TelematicsFilter, root is not defined');
                return;
            }
            // queries = localStorage.getItem('filterManager.jobs') ? JSON.parse(localStorage.getItem('filterManager.jobs').toString()) : {};
            filterAdapter = new FilterAdapter(
                root,
                // @ts-ignore Targeo.Base.Map undefined
                Targeo.Base.Map,
                collectionName,
                filterConfig,
                enableInactiveQueries,
                andOrSelectionEnabled,
                filterManagerIO,
                showButtonRemoveAll,
                showButtonRestoreFilter,
                (queries, andOrSelection, allQueries, changed) => {
                    console.log('TelematicsFilter', collectionName, queries, andOrSelection);
                    debugAllQueries = { ...allQueries };
                    onChange(queries, andOrSelection, allQueries, changed);
                }
            );
        });
    }

    onMount(() => {
        reload();
        return () => {
            if (filterAdapter) filterAdapter.destroy();
        };
    });

    $: {
        forceReload = forceReload;
        if (forceReload) {
            reload();
        }
    }
</script>

<div
    class="telematics-filter-container"
    class:feature_filter_column={$features.feature_filter_column &&
        Object.keys(debugAllQueries).length > 0}
    class:blured={makeBlured}
>
    {#if visibleFilterIcon}
        <div class="telematics-filter-icon-placeholder">
            <!--            <IconFilterAlt />-->
            <TelematicsFilterIconWithStats
                {filteredCount}
                {totalCount}
                query={debugAllQueries}
                bind:filterHidden={$deliveryList.filterHidden}
            />
        </div>
    {/if}
    <div
        style="width: 100%;"
        class:filterHidden={$deliveryList.filterHidden}
        data-filterHidden={$deliveryList.filterHidden}
    >
        {#if $features.feature_ux_improvements && quickFilters.length}
            <div style="display: flex; flex-direction: row; flex-wrap: wrap; padding: 3px 0px; gap: 3px;">
                {#each quickFilters as quickFilter, index}
                    <QuickFilter bind:quickFilter={quickFilter} {index} onSwitch={onQuickFilterSwitch}/>
                {/each}
            </div>
        {/if}
        {#if showQuickFilters}
            <div
                style="display: flex; flex-direction: row; padding: 3px 0px; flex-wrap: wrap; row-gap: 3px;"
            >
                <SearchInputLikeFilter
                    placeholder={$_('Search by address')}
                    query={$filter.jobsToBeCompleted.quickSearch}
                    onChange={q => {
                        $filter.jobsToBeCompleted.quickSearch = q;
                    }}
                    onKeyup={q => {
                        $filter.jobsToBeCompleted.quickSearch = q;
                    }}
                    debounceTime={500}
                />
                <CheckboxLikeFilter
                    label={$_('only_low_quality_address')}
                    bind:checked={$filter.jobsToBeCompleted.onlyLowQuality}
                />
            </div>
        {/if}

        <div bind:this={root} style="width: 100%;">
            <div class="js-modal-colorizer-setup"></div>
            <div class="geocoding-app">
                <div class="telematics-filter-overlay"></div>
            </div>
            <div class="telematics-filter-bar-range-menu"></div>
            <!-- TODO: move it to the better place by creating another component -->
            <div class="js-modal-collection"></div>
            <div
                class="js-filter-group"
                class:filterHideMore={!showButtonAddFilter}
                style="overflow: unset;"
            >
                <div class="js-object-counter"></div>
            </div>
            <div class="background-under-the-filter"></div>
        </div>
    </div>
</div>

<style>
    :global(.js-collection-selector) {
        display: none;
    }
    :global(.js-object-counter) {
        display: none;
    }
    /*.telematics-filter-overlay {*/
    /*    position: fixed;*/
    /*    top: 0;*/
    /*    left: 0;*/
    /*    width: 100vw;*/
    /*    height: 100vh;*/
    /*    background-color: #223a;*/
    /*    z-index: 20199;*/
    /*}*/
    .blured {
        filter: grayscale(1) opacity(0.3);
        pointer-events: none;
    }
    :global(.js-filter-group.filterHideMore .telematics-filter_bar_more__add),
    :global(.js-filter-group.filterHideMore .telematics-filter_bar_more__restore) {
        display: none !important;
        /*background-color: #f92672;*/
    }
    .telematics-filter-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        row-gap: 4px;
    }
    .telematics-filter-icon-placeholder {
        margin-right: 6px;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        box-sizing: border-box;
        gap: 10px;
    }
    .feature_filter_column {
        flex-direction: column !important;
        align-items: flex-start !important;
    }
    .filterHidden {
        display: none;
    }
</style>
